import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, Download, FileSpreadsheet, FileText, AlertCircle, AlertTriangle } from 'lucide-react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { SUPPORTED_CURRENCIES, formatCurrency as sharedFormatCurrency } from '../utils/currency';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useNavigate } from 'react-router-dom';
import { getLocationsByOrgId } from '../utils/locationApi';
import { getRevenueExport, getAllRevenueExport } from '../utils/revenueExportApi';
import LoadingScreen from '../components/LoadingScreen';

const normalizeCurrencyCode = (value, fallback = 'USD') => {
  if (!value || typeof value !== 'string') return fallback;
  const upper = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : fallback;
};

const RevenueExportPage = () => {
  const navigate = useNavigate();
  const { currentOrganization, isLoading: isLoadingUserSettings } = useUserSettingsContext();
  const revenueMapEnabled = useFeatureFlagEnabled('revenue-map');

  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [revenueData, setRevenueData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [includeDateDetails, setIncludeDateDetails] = useState(true);
  const [includeUnmappedData, setIncludeUnmappedData] = useState(true);
  const [targetCurrency, setTargetCurrency] = useState('USD');
  const [currencyMetadata, setCurrencyMetadata] = useState(null);

  // Filter out unmapped data if checkbox is unchecked
  const filteredData = useMemo(() => {
    if (includeUnmappedData) return revenueData;
    return revenueData.filter(row =>
      !((row.accountNumber === 'N/A' || !row.accountNumber) && row.revenueTypeMappedType && row.revenueTypeMappedType.trim() !== '')
    );
  }, [revenueData, includeUnmappedData]);

  // displayData is now just filteredData — grouping is handled server-side via groupByAccount
  const displayData = filteredData;

  const locationCurrency = normalizeCurrencyCode(selectedLocation?.currency);
  const organizationCurrency = normalizeCurrencyCode(currentOrganization?.reportingCurrency);
  const currencyOptions = useMemo(() => (
    Array.from(new Set([locationCurrency, organizationCurrency, ...SUPPORTED_CURRENCIES])).filter(Boolean)
  ), [locationCurrency, organizationCurrency]);

  useEffect(() => {
    if (!selectedLocation) return;
    setTargetCurrency(normalizeCurrencyCode(selectedLocation.currency));
    setCurrencyMetadata(null);
  }, [selectedLocation]);

  // Count items where account number is N/A but mapped type is not blank
  const unmappedAccountCount = useMemo(() => {
    return revenueData.filter(row =>
      (row.accountNumber === 'N/A' || !row.accountNumber) && row.revenueTypeMappedType && row.revenueTypeMappedType.trim() !== ''
    ).length;
  }, [revenueData]);

  // Page size options
  const pageSizeOptions = [25, 50, 100];

  // Redirect if feature flag is disabled
  useEffect(() => {
    if (revenueMapEnabled === false) {
      navigate('/dashboard');
    }
  }, [revenueMapEnabled, navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-dropdown-container')) {
        setShowLocationDropdown(false);
      }
      if (showPageSizeDropdown && !event.target.closest('.pagesize-dropdown-container')) {
        setShowPageSizeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationDropdown, showPageSizeDropdown]);

  // Load initial data: locations
  useEffect(() => {
    if (!currentOrganization || isLoadingUserSettings) {
      return;
    }

    const fetchLocations = async () => {
      try {
        const locationsData = await getLocationsByOrgId(currentOrganization.id);
        if (locationsData && Array.isArray(locationsData) && locationsData.length > 0) {
          setLocations(locationsData);
          setSelectedLocation(locationsData[0]);
        } else {
          setError('No locations found for this organization.');
        }
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError(`Failed to load locations: ${err.message}`);
      }
    };

    fetchLocations();
  }, [currentOrganization, isLoadingUserSettings]);

  // Fetch revenue data
  const fetchRevenueData = async () => {
    if (!selectedLocation || !startDate || !endDate) {
      setError('Please select a location and date range.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getRevenueExport({
        locationId: selectedLocation.id,
        startDate,
        endDate,
        page: pagination.page,
        pageSize: pagination.pageSize,
        groupByAccount: !includeDateDetails,
        targetCurrency
      });

      setRevenueData(result.data || []);
      setCurrencyMetadata(result.currency || null);
      setPagination(result.pagination || { page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setError(`Failed to load revenue data: ${err.message}`);
      setRevenueData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search button click
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchRevenueData();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Fetch data when pagination or grouping changes
  useEffect(() => {
    if (selectedLocation && startDate && endDate && revenueData.length > 0) {
      fetchRevenueData();
    }
  }, [pagination.page, pagination.pageSize, includeDateDetails]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
    setShowPageSizeDropdown(false);
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Fetch all data for export (grouping handled server-side)
      const result = await getAllRevenueExport({
        locationId: selectedLocation.id,
        startDate,
        endDate,
        groupByAccount: !includeDateDetails,
        targetCurrency
      });

      const exportData = result.data || [];

      if (exportData.length === 0) {
        setError('No data to export.');
        return;
      }

      // Create CSV content
      const exportCurrency = result.currency?.targetCurrency || targetCurrency;
      const headers = includeDateDetails
        ? ['Effective Date', 'Account Number', 'Account Name', 'Revenue Type', 'Mapped Type', `Amount (${exportCurrency})`]
        : ['Account Number', 'Account Name', 'Revenue Type', 'Mapped Type', `Amount (${exportCurrency})`];
      const rows = exportData.map(row => includeDateDetails
        ? [new Date(row.effectiveDate).toLocaleDateString('en-US', { timeZone: 'UTC' }), row.accountNumber, row.accountName, row.revenueTypeName || '', row.revenueTypeMappedType || '', row.amount]
        : [row.accountNumber, row.accountName, row.revenueTypeName || '', row.revenueTypeMappedType || '', row.amount]
      );

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `revenue_export_${selectedLocation.name}_${startDate}_${endDate}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting to CSV:', err);
      setError(`Failed to export: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Export to Excel (using CSV format with .xlsx extension for simplicity)
  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Fetch all data for export (grouping handled server-side)
      const result = await getAllRevenueExport({
        locationId: selectedLocation.id,
        startDate,
        endDate,
        groupByAccount: !includeDateDetails,
        targetCurrency
      });

      const exportData = result.data || [];

      if (exportData.length === 0) {
        setError('No data to export.');
        return;
      }

      // Create CSV content (Excel can open CSV files)
      const exportCurrency = result.currency?.targetCurrency || targetCurrency;
      const headers = includeDateDetails
        ? ['Effective Date', 'Account Number', 'Account Name', 'Revenue Type', 'Mapped Type', `Amount (${exportCurrency})`]
        : ['Account Number', 'Account Name', 'Revenue Type', 'Mapped Type', `Amount (${exportCurrency})`];
      const rows = exportData.map(row => includeDateDetails
        ? [new Date(row.effectiveDate).toLocaleDateString('en-US', { timeZone: 'UTC' }), row.accountNumber, row.accountName, row.revenueTypeName || '', row.revenueTypeMappedType || '', row.amount]
        : [row.accountNumber, row.accountName, row.revenueTypeName || '', row.revenueTypeMappedType || '', row.amount]
      );

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download file with .xlsx extension
      const blob = new Blob([csvContent], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `revenue_export_${selectedLocation.name}_${startDate}_${endDate}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(`Failed to export: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount, currency) => sharedFormatCurrency(amount || 0, currency);

  // Show loading indicator if user settings are loading
  if (isLoadingUserSettings) {
    return <LoadingScreen message="Loading User Settings" />;
  }

  // Render nothing or a placeholder while checking the flag/redirecting
  if (revenueMapEnabled === null || revenueMapEnabled === false) {
    return <div className="flex justify-center items-center h-full">Checking feature availability...</div>;
  }

  return (
    <div className="flex flex-col p-6 w-full h-full">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col overflow-visible">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            Revenue Export {currentOrganization ? `- ${currentOrganization.name}` : ''}
          </h1>
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {/* Location Selector */}
          <div className="relative location-dropdown-container">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <button
              className="flex items-center justify-between gap-2 px-4 py-2 rounded-md border border-gray-200 bg-white text-sm font-medium w-full"
              onClick={() => setShowLocationDropdown(!showLocationDropdown)}
            >
              <span className="truncate">
                {selectedLocation ? selectedLocation.name : locations.length > 0 ? 'Select Location' : 'No Locations'}
              </span>
              <ChevronDown size={16} />
            </button>
            {showLocationDropdown && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-50 overflow-y-auto" style={{ maxHeight: '400px' }}>
                {locations.length > 0 ? (
                  locations.map(location => (
                    <button
                      key={location.id}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => {
                        setSelectedLocation(location);
                        setShowLocationDropdown(false);
                        setRevenueData([]);
                      }}
                    >
                      {location.name}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">No locations available</div>
                )}
              </div>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              className="px-4 py-2 rounded-md border border-gray-200 text-sm w-full"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              className="px-4 py-2 rounded-md border border-gray-200 text-sm w-full"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select
              className="px-4 py-2 rounded-md border border-gray-200 text-sm w-full bg-white"
              value={targetCurrency}
              onChange={(e) => {
                setTargetCurrency(e.target.value);
                setRevenueData([]);
                setCurrencyMetadata(null);
              }}
            >
              {currencyOptions.map(currency => (
                <option key={currency} value={currency}>
                  {currency}{currency === locationCurrency ? ' (location)' : currency === organizationCurrency ? ' (org)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <div className="flex items-end">
            <button
              className="px-6 py-2 bg-orange-500 text-white rounded-md text-sm font-medium hover:bg-orange-600 w-full"
              onClick={handleSearch}
              disabled={!selectedLocation || !startDate || !endDate || isLoading}
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span>{error}</span>
            <button className="ml-auto text-red-500 hover:text-red-700" onClick={() => setError(null)}>
              Dismiss
            </button>
          </div>
        )}

        {currencyMetadata?.conversionApplied && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            Amounts converted from source revenue currency to {currencyMetadata.targetCurrency} using {String(currencyMetadata.granularity || 'DAILY').toLowerCase()} rates by effective date.
            {currencyMetadata.staleRates?.length > 0 ? ' Some rows used the latest prior rate.' : ''}
          </div>
        )}

        {/* Export Buttons, Options, and Page Size Selector */}
        {revenueData.length > 0 && (
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2 items-center">
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 flex items-center gap-2"
                onClick={exportToCSV}
                disabled={isExporting}
              >
                <FileText size={16} />
                {isExporting ? 'Exporting...' : 'Export CSV'}
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
                onClick={exportToExcel}
                disabled={isExporting}
              >
                <FileSpreadsheet size={16} />
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </button>

              {/* Include Date Details Checkbox */}
              <label className="flex items-center gap-2 ml-4 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={includeDateDetails}
                  onChange={(e) => {
                    setIncludeDateDetails(e.target.checked);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                Include Date Details
              </label>

              {/* Include Unmapped Data Checkbox */}
              <label className="flex items-center gap-2 ml-4 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={includeUnmappedData}
                  onChange={(e) => setIncludeUnmappedData(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                Include Unmapped Data
              </label>

              {/* Unmapped Account Warning - links to Revenue Types page */}
              {unmappedAccountCount > 0 && (
                <button
                  className="flex items-center gap-1.5 ml-4 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm hover:bg-amber-100 transition-colors"
                  title={`${unmappedAccountCount} item(s) have a mapped type but no account number. Click to go to Revenue Mapping.`}
                  onClick={() => navigate(`/revenue-types?locationId=${selectedLocation?.id}`)}
                >
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <span>{unmappedAccountCount} unmapped account{unmappedAccountCount !== 1 ? 's' : ''}</span>
                </button>
              )}
            </div>

            {/* Page Size Selector */}
            <div className="relative pagesize-dropdown-container">
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-gray-200 bg-white text-sm font-medium"
                onClick={() => setShowPageSizeDropdown(!showPageSizeDropdown)}
              >
                Show {pagination.pageSize}
                <ChevronDown size={16} />
              </button>
              {showPageSizeDropdown && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 w-full">
                  {pageSizeOptions.map(size => (
                    <button
                      key={size}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={() => handlePageSizeChange(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
                <p className="text-gray-600">Loading revenue data...</p>
              </div>
            </div>
          ) : revenueData.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-700 mb-1">No Data</h3>
                <p className="text-gray-500">Select a location and date range to view revenue data</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-auto flex-1">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {includeDateDetails && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Effective Date
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Account Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mapped Type
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayData.map((row, index) => {
                      const isUnmappedAccount = (row.accountNumber === 'N/A' || !row.accountNumber) && row.revenueTypeMappedType && row.revenueTypeMappedType.trim() !== '';
                      return (
                      <tr key={index} className={`hover:bg-gray-50 ${isUnmappedAccount ? 'bg-amber-50' : ''}`}>
                        {includeDateDetails && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(row.effectiveDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className="flex items-center gap-1.5">
                            {isUnmappedAccount && <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
                            {row.accountNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.accountName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.revenueTypeName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.revenueTypeMappedType || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(row.amount, row.targetCurrency || currencyMetadata?.targetCurrency || targetCurrency)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
                  <div className="flex items-center text-sm text-gray-700">
                    Showing {(pagination.page - 1) * pagination.pageSize + 1} to{' '}
                    {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount}{' '}
                    results
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 rounded-md border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </button>
                    <div className="flex gap-1">
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(pageNum => (
                        <button
                          key={pageNum}
                          className={`px-3 py-1 rounded-md text-sm font-medium ${
                            pageNum === pagination.page
                              ? 'bg-orange-500 text-white'
                              : 'border border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>
                    <button
                      className="px-3 py-1 rounded-md border border-gray-200 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueExportPage;
